import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useStoreApi,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FlowZoomBridge, useFlowZoom } from '../utils/flowZoom';
import registry from '../data/entityRegistry.gen.json';
import { agentOsProblems, sideEffectKind, portsOf } from '../data/agentosGraph';
import { requirementProblems, requirementRefusals, requirementSummary } from '../data/requirements';
import { useEntityDoc, useEntityList, updateEntity } from '../data/entities';
import { queueFanoutJob, useJob } from '../data/firestore';
import NewWorkflowForm from '../components/NewWorkflowForm';
import RequirementsEditor, { StatusChip } from '../components/RequirementsEditor';
import PipelineTestsCard from '../components/PipelineTestsCard';
import { describeGraph, requirementBlocks } from '../data/describe';
import { FOCUS_EVENT } from '../utils/pageControls';
import { auth } from '../firebaseConfig';
import { useTenant } from '../data/entities';
import CodeBlockFields from '../components/nqube/CodeBlockFields';
import UploadBlockFields from '../components/nqube/UploadBlockFields';
import RunLogsPanel from '../components/nqube/RunLogsPanel';
import DryRunPanel from '../components/nqube/DryRunPanel';
import HistoryPanel from '../components/nqube/HistoryPanel';
import SchedulePanel from '../components/nqube/SchedulePanel';
import { pipelineApi, copyText } from '../components/nqube/api';

// Visual pipeline editor: React Flow canvas over the pipeline's graph
// ({ blocks, connections } — the exact shape backend/runJob.js executes).
// The palette and config forms render from the generated block catalog, so a
// new block type in backend/shared/blocks/catalog.js shows up here after
// `node scripts/generate-entities.mjs` with zero editor changes.
//
// During a run the canvas is live: the runner publishes per-block state to
// jobs/{id}.liveBlocks (queued → running → done/skipped/error) and the
// custom node/edge components below render it as processing lights and
// data packets streaming along the wires.
//
// STREAMING mode (pipeline.mode === 'streaming') goes further: blocks are
// always-on Fargate tasks, wires are real Redis queues, and the depth chips
// on the edges are ACTUAL queue lengths (pipeline.liveWires, polled by the
// stream worker) — when a number moves here, real documents are moving.
const BLOCK_TYPES = registry.blockTypes || {};
const EXECUTION_PROFILES = registry.executionProfiles || {};
// Per-block compute classes (shared/nqube/compute.js) — where a block runs.
const COMPUTE_CLASSES = [
  ['shared', 'Shared — in the pipeline runner (default)'],
  ['lite', 'Lite — Lambda, pay per ms'],
  ['standard', 'Standard — own Fargate task'],
  ['gpu', 'GPU — Managed Instances'],
  ['heavy', 'Heavy — beyond Fargate limits'],
];

const CATEGORY_COLOR = { input: '#3b82f6', logic: '#2dd4bf', transform: '#a78bfa', control: '#f59e0b', output: '#34d399', design: '#94a3b8' };
const CATEGORY_ORDER = [['input', 'Read in'], ['logic', 'Logic (no model)'], ['transform', 'Transform'], ['control', 'Gate & control'], ['output', 'Write out'], ['design', 'Design']];

const STATUS_LABEL = { queued: 'queued', running: 'processing', done: 'done', skipped: 'skipped', error: 'failed' };
const STREAM_LIGHT = { launching: 'queued', running: 'running', stopped: 'skipped', error: 'error' };

// Container stages report progress (liveBlocks[id].progress): the pre-flight
// counts its checks, the train stage its epochs, anything else a pct/step.
function NodeProgress({ p }) {
  if (p.stage === 'preflight' && Array.isArray(p.checks)) {
    const fails = p.totalFailed || 0;
    return (
      <div className="nq-node-progress">
        <span>{p.currentCategory || 'checking'} · {p.checks.length}{p.totalChecks ? `/${p.totalChecks}` : ''}</span>
        {fails > 0 && <span className="nq-node-progress-bad"> · {fails} fail</span>}
      </div>
    );
  }
  if (p.stage === 'train' && p.epochs) {
    return (
      <div className="nq-node-progress">
        <span>epoch {p.epoch}/{p.epochs}{Number.isFinite(p.valLoss) ? ` · val ${Number(p.valLoss).toFixed(3)}` : ''}</span>
        <span className="nq-node-bar"><span style={{ width: `${Math.min(100, p.pct || 0)}%` }} /></span>
      </div>
    );
  }
  const pct = Number(p.pct);
  if (!Number.isFinite(pct) && !p.step) return null;
  return (
    <div className="nq-node-progress">
      <span>{p.step || p.stage || ''}{Number.isFinite(pct) ? ` · ${Math.round(pct)}%` : ''}</span>
      {Number.isFinite(pct) && <span className="nq-node-bar"><span style={{ width: `${Math.min(100, pct)}%` }} /></span>}
    </div>
  );
}

function NQubeNode({ data, selected }) {
  const def = BLOCK_TYPES[data.blockType] || {};
  const color = CATEGORY_COLOR[def.category] || '#64748b';
  const run = data.run;
  const status = run?.status;
  const stream = data.stream; // streaming-mode runtime: { status, processed, errors, lastError }
  const ports = portsOf({ type: data.blockType, config: data.config }, def);
  const cls = [
    'nq-node',
    status ? `nq-${status}` : '',
    run?.gateClosed ? 'nq-gate-closed' : '',
    selected ? 'nq-selected' : '',
    def.category === 'design' ? 'nq-placeholder' : '',
    data.highlight ? 'nq-flagged' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <Handle type="target" position={Position.Left} className="nq-handle" />
      <div className="nq-node-head">
        <span className="nq-cat-dot" style={{ background: color }} />
        <span className="nq-node-title">{data.label}</span>
        {data.timer?.everyMinutes > 0 && <span className="nq-node-timer" title={`auto-runs every ${timerLabel(data.timer.everyMinutes)} (this block and downstream)`}>⏱</span>}
        {status && <span className={`nq-light nq-light-${run?.gateClosed ? 'skipped' : status}`} />}
        {stream?.status && <span className={`nq-light nq-light-${STREAM_LIGHT[stream.status] || 'skipped'}`} />}
      </div>
      {data.label !== (def.label || data.blockType) && <div className="nq-node-type">{def.label || data.blockType}</div>}
      {status && (
        <div className="nq-node-status">
          {run.gateClosed ? '⨂ branch closed' : run.replayed ? 'replayed' : run.port ? `→ ${run.port}` : STATUS_LABEL[status] || status}
          {status === 'done' && !run.gateClosed && typeof run.ms === 'number' ? ` · ${(run.ms / 1000).toFixed(1)}s` : ''}
        </div>
      )}
      {stream?.status && (
        <div className="nq-node-status">
          {stream.status}
          {typeof stream.processed === 'number' ? ` · ${stream.processed} docs` : ''}
          {stream.errors ? ` · ${stream.errors} err` : ''}
        </div>
      )}
      {run?.progress && status === 'running' && <NodeProgress p={run.progress} />}
      {run?.preview && !run.gateClosed && <div className="nq-node-preview">{run.preview}</div>}
      {(run?.error || stream?.lastError) && <div className="nq-node-error">{run?.error || stream?.lastError}</div>}
      {ports ? (
        // Gate blocks: one labelled source handle per port (primary on top).
        // The handle id becomes the connection's fromPort (see toGraph).
        ports.map((p, i) => (
          <Handle
            key={p}
            id={p}
            type="source"
            position={Position.Right}
            className={`nq-handle nq-handle-port nq-handle-${i === 0 ? 'primary' : 'secondary'}${String(run?.port || '').split('+').includes(p) || (i === 0 && run && !run.port && !run.gateClosed && status === 'done') ? ' is-live' : ''}`}
            style={{ top: `${ports.length === 1 ? 50 : 22 + (i * 56) / (ports.length - 1)}%` }}
            title={`${p} port`}
          >
            <span className="nq-port-label">{p}</span>
          </Handle>
        ))
      ) : (
        <Handle type="source" position={Position.Right} className="nq-handle" />
      )}
    </div>
  );
}

// Edge that visualizes data movement: animated packets while upstream is
// producing (or output is queued for the next block), solid green once the
// hand-off completed, dashed + dim for branches that died at a gate.
// Waypoints (connection.waypoints[]) bend a wire around blocks: the path is
// a Catmull-Rom spline through them; drag a dot to move it, double-click the
// dot to remove it, double-click the wire to add one. Pure layout — the
// runner ignores them.
function splinePath(points) {
  if (points.length < 3) return `M ${points[0].x} ${points[0].y} L ${points[points.length - 1].x} ${points[points.length - 1].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i]; const p1 = points[i]; const p2 = points[i + 1]; const p3 = points[i + 2] || p2;
    d += ` C ${p1.x + (p2.x - p0.x) / 6} ${p1.y + (p2.y - p0.y) / 6}, ${p2.x - (p3.x - p1.x) / 6} ${p2.y - (p3.y - p1.y) / 6}, ${p2.x} ${p2.y}`;
  }
  return d;
}
function PacketEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, label }) {
  const waypoints = Array.isArray(data?.waypoints) && data.waypoints.length ? data.waypoints : null;
  const bez = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const pts = waypoints ? [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }] : null;
  const path = pts ? splinePath(pts) : bez[0];
  const mid = pts ? pts[Math.floor(pts.length / 2)] : null;
  const labelX = mid ? mid.x : bez[1];
  const labelY = mid ? mid.y - 14 : bez[2];
  const rf = useReactFlow();
  const dragWaypoint = (i) => (e) => {
    if (!data?.onWaypoints) return;
    e.stopPropagation(); e.preventDefault();
    const move = (ev) => {
      const p = rf.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      data.onWaypoints(id, waypoints.map((w, j) => (j === i ? { x: Math.round(p.x), y: Math.round(p.y) } : w)));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const state = data?.state || 'idle';
  const depth = data?.depth; // streaming: REAL Redis queue length for this wire
  const style = {
    idle: { stroke: 'rgba(148,163,184,0.45)', strokeWidth: 1.6 },
    flowing: { stroke: '#22d3ee', strokeWidth: 2.4 },
    done: { stroke: 'rgba(52,211,153,0.85)', strokeWidth: 2 },
    dead: { stroke: 'rgba(148,163,184,0.22)', strokeWidth: 1.4, strokeDasharray: '5 5' },
  }[state];
  return (
    <>
      <BaseEdge id={id} path={path} style={style} />
      {state === 'flowing' && [0, 0.45, 0.9].map((begin) => (
        <circle key={begin} r="3.5" className="nq-packet">
          <animateMotion dur="1.35s" begin={`${begin}s`} repeatCount="indefinite" path={path} />
        </circle>
      ))}
      {depth != null && (
        <g transform={`translate(${labelX}, ${labelY})`}>
          <rect x="-16" y="-11" width="32" height="20" rx="10" className={`nq-depth${depth > 0 ? ' nq-depth-hot' : ''}`} />
          <text textAnchor="middle" dominantBaseline="central" className="nq-depth-text">{depth}</text>
        </g>
      )}
      {depth == null && label && (
        <text x={labelX} y={labelY - 6} textAnchor="middle" className={`nq-edge-port nq-edge-port-${state}`}>{label}</text>
      )}
      {waypoints && data?.onWaypoints && (
        <EdgeLabelRenderer>
          {waypoints.map((w, i) => (
            <div
              key={i}
              className="nq-waypoint nodrag nopan"
              style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${w.x}px, ${w.y}px)`, pointerEvents: 'all' }}
              onPointerDown={dragWaypoint(i)}
              onDoubleClick={(e) => { e.stopPropagation(); data.onWaypoints(id, waypoints.filter((_, j) => j !== i)); }}
              title="Drag to bend the wire · double-click to remove"
            />
          ))}
        </EdgeLabelRenderer>
      )}
    </>
  );
}

// `port` = the edge's fromPort (undefined = primary). An edge is dead when the
// upstream output left on a different port than the edge hangs off.
const PRIMARY_PORTS = new Set(['pass', 'approve']);
function edgeState(src, tgt, jobActive, port) {
  if (!src && !tgt) return 'idle';
  if (src?.gateClosed || src?.status === 'skipped' || tgt?.status === 'skipped') return 'dead';
  if (src?.status === 'done') {
    // A split gate reports 'valid+invalid': both exits are live.
    const left = String(src.port || 'primary').split('+');
    const want = !port || PRIMARY_PORTS.has(port) ? 'primary' : port;
    if (!left.includes(want)) return 'dead';
  }
  if (src?.status === 'running') return 'flowing';
  if (src?.status === 'done' && (tgt?.status === 'running' || (jobActive && tgt?.status === 'queued'))) return 'flowing';
  if (src?.status === 'done') return 'done';
  return 'idle';
}

const NODE_TYPES = { nqube: NQubeNode };
const EDGE_TYPES = { packet: PacketEdge };

const toNodes = (graph) =>
  (graph?.blocks || []).map((b) => ({
    id: b.id,
    type: 'nqube',
    position: b.position || { x: 80, y: 80 },
    data: {
      label: `${b.label || BLOCK_TYPES[b.type]?.label || b.type}`,
      blockType: b.type,
      config: b.config || {},
      compute: b.compute || null, // per-block compute class { class, size }
      classification: b.classification || null, // 'sensitive' → integrity.sensitive-stays-local reads it
      retry: b.retry || null, // { maxAttempts, backoffMs } — runJob retries a failed block this many times
      timer: b.timer || null, // { everyMinutes, timeoutMinutes } — auto-run this block (+ downstream) on an interval; wall clock per block
    },
  }));

// Pipeline-level summary of the blocks' timers — what the schedule worker
// watches (`timers.enabled`) and fires from (one cursor per block).
export const timersOf = (nodes, by = null) => {
  const blocks = {};
  for (const n of nodes) {
    const every = parseInt(n.data?.timer?.everyMinutes, 10);
    if (Number.isFinite(every) && every > 0) blocks[n.id] = { everyMinutes: every, label: n.data.label || n.id };
  }
  return Object.keys(blocks).length ? { enabled: true, blocks, by } : { enabled: false, blocks: {} };
};

// fromPort ↔ React Flow sourceHandle. Gate nodes render one handle per port
// (id = port name); plain nodes have an anonymous handle (no fromPort).
const toEdges = (graph) =>
  (graph?.connections || []).map((c) => ({
    id: c.id || `${c.from}-${c.to}${c.fromPort ? `-${c.fromPort}` : ''}`,
    source: c.from,
    target: c.to,
    ...(c.fromPort ? { sourceHandle: c.fromPort, label: c.fromPort } : {}),
    ...(Array.isArray(c.waypoints) && c.waypoints.length ? { data: { waypoints: c.waypoints } } : {}),
  }));

const toGraph = (nodes, edges) => ({
  blocks: nodes.map((n) => ({
    id: n.id,
    type: n.data.blockType,
    label: n.data.label,
    position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
    config: n.data.config || {},
    ...(n.data.compute?.class && n.data.compute.class !== 'shared' ? { compute: n.data.compute } : {}),
    ...(n.data.classification ? { classification: n.data.classification } : {}),
    ...(n.data.retry?.maxAttempts > 1 ? { retry: n.data.retry } : {}),
    ...((n.data.timer?.everyMinutes > 0 || n.data.timer?.timeoutMinutes > 0) ? { timer: n.data.timer } : {}),
  })),
  connections: edges.map((e) => ({
    id: e.id,
    from: e.source,
    to: e.target,
    ...(e.sourceHandle ? { fromPort: e.sourceHandle } : {}),
    ...(Array.isArray(e.data?.waypoints) && e.data.waypoints.length ? { waypoints: e.data.waypoints } : {}),
  })),
});

export default function PipelineEditor() {
  const { orgId, workspaceId, pipelineId } = useParams();
  const scope = useMemo(() => ({ org: orgId, workspace: workspaceId }), [orgId, workspaceId]);
  const { doc: pipeline } = useEntityDoc('pipeline', scope, pipelineId);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const canvasRef = useRef(null);
  const nodeKey = nodes.map((n) => `${n.id}:${Math.round((n.position?.x || 0) / 80)}:${Math.round((n.position?.y || 0) / 80)}`).join('|');
  const { minZoom, maxZoom, onInit, placeRef } = useFlowZoom(canvasRef, {
    blockWidth: 180,
    blockHeight: 78,
    nodeKey,
  });
  const [caps, setCaps] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [flagged, setFlagged] = useState([]); // block ids a requirement row pointed at
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runError, setRunError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [sideTab, setSideTab] = useState('run'); // run | logs | history | schedule
  const [wireFrom, setWireFrom] = useState('last'); // where a palette click wires the new block from: last | selected | none
  const [logBlock, setLogBlock] = useState(null);
  const tenant = useTenant();
  const canEditSchedule = ['admin', 'owner'].includes(tenant?.role);
  const { job: runJob, tasks: runTasks } = useJob(pipeline?.lastRunJobId || null);
  // A test run (Tests card / test_pipeline) takes over the live overlay
  // while it is active; afterwards the canvas shows the last real run again.
  const { job: testJob, tasks: testTasks } = useJob(pipeline?.lastTest?.jobId || null);
  const testActive = !!testJob && ['queued', 'planning', 'running', 'stitching'].includes(testJob.status);
  const job = testActive ? testJob : runJob;
  const tasks = testActive ? testTasks : runTasks;
  // Org policy the agentOS check runs under: the pipeline's env plus the
  // org's Integrations (webhook capabilities must bind to one in scope).
  const { docs: integrations } = useEntityList('integration', { org: orgId });
  // The org's own requirements for this env (Administration → Workflow
  // requirements) ride on the policy as read-only rows.
  const { doc: org } = useEntityDoc('org', {}, orgId);
  // integrations stays null until loaded — the allowlist check is skipped
  // rather than flashing "unbound" on every webhook while the list arrives.
  const policy = useMemo(() => ({
    env: pipeline?.env || 'sandbox',
    integrations: integrations || null,
    requirements: org?.pipelinePolicy?.[pipeline?.env || 'sandbox']?.requirements || [],
  }), [pipeline?.env, integrations, org?.pipelinePolicy]);

  // Hydrate the canvas once from Firestore (afterwards local state is truth
  // until save — a live re-sync would fight the user's drag in progress).
  useEffect(() => {
    if (pipeline && !loaded) {
      const nextCaps = Array.isArray(pipeline.capabilities) ? pipeline.capabilities : [];
      setNodes(toNodes(pipeline.graph));
      setEdges(toEdges(pipeline.graph));
      setCaps(nextCaps);
      setReqs(Array.isArray(pipeline.requirements) ? pipeline.requirements : []);
      const problems = agentOsProblems(pipeline.graph, nextCaps, policy);
      if (problems.length) setRunError(problems.join(' '));
      setLoaded(true);
    }
  }, [pipeline, loaded, policy]);

  // The design-time verdict: every requirement judged against the canvas as
  // it is right now (requirements.js). Structural misses refuse a run
  // outside sandbox; sandbox only shows them.
  const reqResults = useMemo(
    () => requirementProblems(toGraph(nodes, edges), reqs, policy, { lastRunMs: pipeline?.lastRun?.ms || null }),
    [nodes, edges, reqs, policy, pipeline?.lastRun?.ms]
  );
  const reqSummary = requirementSummary(reqResults);
  // What NoVA reads about the canvas (data/describe.js, the same text the
  // describe_pipeline composer returns): React Flow's DOM is opaque to the
  // screen outline, so the editor describes the graph itself.
  const novaDescribe = useMemo(
    () => (loaded ? describeGraph(toGraph(nodes, edges), { requirements: reqs, policy, capabilities: caps, selected, lastRun: pipeline?.lastRun || null, blockTypes: BLOCK_TYPES, maxChars: 950, name: pipeline?.name || '' }) : ''),
    [loaded, nodes, edges, reqs, policy, caps, selected, pipeline?.lastRun, pipeline?.name]
  );

  // NoVA's finger on the canvas: the focus_canvas page control dispatches
  // `nova:focus` (utils/pageControls.js); block ids ring like a requirement
  // row's "show", a requirement id rings the blocks it judges, "select"
  // opens the config panel, "fit" frames the graph.
  const flowRef = useRef(null);
  const onFlowInit = useCallback((instance) => { flowRef.current = instance; onInit(instance); }, [onInit]);
  useEffect(() => {
    const onFocus = (e) => {
      const d = e.detail || {};
      const graph = toGraph(nodes, edges);
      const known = new Set(graph.blocks.map((b) => b.id));
      let ids = [];
      if (d.ids?.[0] === 'all') ids = [...known];
      else {
        for (const id of d.ids || []) {
          if (known.has(id)) ids.push(id);
          else if (reqs.some((r) => r.id === id)) ids.push(...requirementBlocks(graph, reqs, policy, id));
          else {
            const byLabel = graph.blocks.find((b) => String(b.label || '').toLowerCase() === id.toLowerCase());
            if (byLabel) ids.push(byLabel.id);
          }
        }
      }
      ids = [...new Set(ids)];
      if (d.action === 'fit') {
        flowRef.current?.fitView?.({ padding: 0.2, duration: 400, ...(ids.length && d.ids?.[0] !== 'all' ? { nodes: ids.map((id) => ({ id })) } : {}) });
        d.reply = { ok: true, message: ids.length && d.ids?.[0] !== 'all' ? `Framed ${ids.join(', ')}.` : 'Framed the whole workflow.' };
        return;
      }
      if (!ids.length) { d.reply = { ok: false, error: `Nothing on this canvas matches "${(d.ids || []).join(', ')}" — use a block id or requirement id from the Describe line.` }; return; }
      if (d.action === 'select') {
        setSelected(ids[0]); setFlagged([]);
        flowRef.current?.fitView?.({ padding: 0.5, duration: 400, nodes: [{ id: ids[0] }] });
        d.reply = { ok: true, message: `Opened ${ids[0]}'s config.` };
        return;
      }
      setFlagged(ids); setSelected(null);
      flowRef.current?.fitView?.({ padding: 0.4, duration: 400, nodes: ids.map((id) => ({ id })) });
      d.reply = { ok: true, message: `Highlighted ${ids.join(', ')}.`, blockIds: ids };
    };
    window.addEventListener(FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(FOCUS_EVENT, onFocus);
  }, [nodes, edges, reqs, policy]);

  const onNodesChange = useCallback((changes) => {
    setNodes((ns) => applyNodeChanges(changes, ns));
    if (changes.some((c) => c.type !== 'select')) setDirty(true);
  }, []);
  const onEdgesChange = useCallback((changes) => {
    setEdges((es) => applyEdgeChanges(changes, es));
    if (changes.some((c) => c.type !== 'select')) setDirty(true);
  }, []);
  const onConnect = useCallback((conn) => {
    // Edge ids carry the port so pass+fail from one gate to one block are
    // two distinct edges; the port is shown as the edge label.
    const id = `${conn.source}-${conn.target}${conn.sourceHandle ? `-${conn.sourceHandle}` : ''}`;
    setEdges((es) => addEdge({ ...conn, id, ...(conn.sourceHandle ? { label: conn.sourceHandle } : {}) }, es));
    setDirty(true);
  }, []);

  // New blocks land to the RIGHT of the current last block (reading order),
  // never stacked on top of it, and are wired FROM it: a palette click
  // extends the chain, so two clicks make a runnable two-step pipeline
  // instead of two islands. Delete the edge to branch differently.
  const lastBlock = (ns) => (ns.length ? ns.reduce((a, b) => ((b.position?.x || 0) > (a.position?.x || 0) ? b : a), ns[0]) : null);
  const nextPosition = (ns) => {
    const last = lastBlock(ns);
    if (!last) return { x: 80, y: 120 };
    return { x: (last.position?.x || 0) + 230, y: last.position?.y || 120 };
  };
  const addBlock = (type) => {
    const id = `b${Date.now().toString(36)}`;
    const def = BLOCK_TYPES[type];
    const from = wireFrom === 'none' ? null : wireFrom === 'selected' ? nodes.find((n) => n.id === selected) || null : lastBlock(nodes);
    const pos = wireFrom === 'selected' && from ? { x: (from.position?.x || 0) + 230, y: (from.position?.y || 0) + (edges.some((e) => e.source === from.id) ? 110 : 0) } : nextPosition(nodes);
    setNodes((ns) => [
      ...ns,
      ...toNodes({ blocks: [{ id, type, label: def?.label || type, position: pos }] }),
    ]);
    if (from && !/^output\./.test(from.data?.blockType || '')) {
      setEdges((es) => [...es, ...toEdges({ connections: [{ id: `${from.id}-${id}`, from: from.id, to: id }] })]);
    }
    setSelected(id);
    setDirty(true);
  };

  // Starter graph: the smallest complete, runnable, gated pipeline.
  const addStarter = () => {
    const t = Date.now().toString(36);
    const ids = [`in${t}`, `llm${t}`, `rev${t}`, `out${t}`];
    const types = ['input.text', 'transform.llm', 'control.review', 'output.webhook'];
    const blocks = types.map((type, i) => ({
      id: ids[i], type, label: BLOCK_TYPES[type]?.label || type, position: { x: 80 + i * 230, y: 120 },
      config: type === 'input.text' ? { text: 'Hello from nQube — replace me with real input.' }
        : type === 'transform.llm' ? { prompt: 'Summarize the input in one sentence.' } : {},
    }));
    setNodes(toNodes({ blocks }));
    setEdges(toEdges({ connections: ids.slice(1).map((to, i) => ({ id: `${ids[i]}-${to}`, from: ids[i], to })) }));
    setSelected(ids[3]);
    setDirty(true);
  };

  // Per-block clone: same type/config/compute, a new id, placed under the
  // original, not wired (the copy is a starting point, not a fork of the flow).
  const duplicateBlock = (node) => {
    const id = `${node.id.replace(/-c[a-z0-9]{3,6}$/, '')}-c${Date.now().toString(36).slice(-4)}`;
    const copy = { ...node, id, selected: false, position: { x: node.position.x, y: node.position.y + 120 }, data: { ...node.data, label: `${node.data.label} (copy)`, config: { ...(node.data.config || {}) }, run: undefined, stream: undefined, highlight: false } };
    setNodes((ns) => [...ns, copy]);
    setSelected(id);
    setDirty(true);
  };
  const setWaypoints = useCallback((edgeId, waypoints) => {
    setEdges((es) => es.map((e) => (e.id === edgeId ? { ...e, data: { ...(e.data || {}), waypoints } } : e)));
    setDirty(true);
  }, []);
  const onEdgeDoubleClick = useCallback((event, edge) => {
    const p = flowRef.current?.screenToFlowPosition?.({ x: event.clientX, y: event.clientY });
    if (!p) return;
    setEdges((es) => es.map((e) => (e.id === edge.id ? { ...e, data: { ...(e.data || {}), waypoints: [...(e.data?.waypoints || []), { x: Math.round(p.x), y: Math.round(p.y) }] } } : e)));
    setDirty(true);
  }, []);

  // Dry run (POST …/dry-run): the unsaved draft runs in-process with no side
  // effects and nothing dequeued; `sampleCount` caps what readers peek.
  const [dry, setDry] = useState(null); // { busy, result, error }
  const dryRun = async (callInput, sampleCount) => {
    setDry({ busy: true });
    try {
      let input = callInput;
      try { input = JSON.parse(callInput); } catch { /* plain text */ }
      const r = await pipelineApi(orgId, workspaceId, pipelineId).post('/dry-run', { callInput: input, graph: toGraph(nodes, edges), sampleCount });
      setDry({ result: r });
    } catch (e) { setDry({ error: e.message }); }
  };
  const dryBlocks = useMemo(() => {
    const r = dry?.result;
    if (!r) return null;
    const out = {};
    for (const id of Object.keys(r.ports || {})) {
      const port = r.ports[id];
      const v = r.outputs?.[id];
      const preview = v == null ? '' : (typeof v.text === 'string' ? v.text : JSON.stringify(v)).replace(/\s+/g, ' ').trim().slice(0, 90);
      out[id] = port === null ? { status: 'done', gateClosed: true, preview: 'branch closed' } : { status: 'done', preview, ...(port !== 'primary' ? { port } : {}) };
    }
    for (const n of nodes) if (!out[n.id]) out[n.id] = { status: 'skipped' };
    return out;
  }, [dry, nodes]);

  const graphError = () => {
    const graph = toGraph(nodes, edges);
    const problems = agentOsProblems(graph, caps, policy);
    const refusals = requirementRefusals(requirementProblems(graph, reqs, policy, { lastRunMs: pipeline?.lastRun?.ms || null }));
    return [...problems, ...refusals].join(' ');
  };

  // Autosave: 1.5s after the last edit. Drafts always persist — a graph that
  // still fails the gate rules (e.g. a webhook with no capability yet) is
  // saved as-is with the problem shown inline; the rules block RUNNING, and
  // the backend re-asserts them (`assertAgentOsGraph`) before any step runs.
  useEffect(() => {
    if (!dirty || !loaded || saving) return undefined;
    const t = setTimeout(() => {
      setSaving(true);
      updateEntity('pipeline', scope, pipelineId, { graph: toGraph(nodes, edges), capabilities: caps, requirements: reqs, timers: timersOf(nodes, auth.currentUser?.uid || null), updatedBy: auth.currentUser?.uid || null })
        .then(() => { setDirty(false); setRunError(graphError()); })
        .catch((e) => setRunError(e.message))
        .finally(() => setSaving(false));
    }, 1500);
    return () => clearTimeout(t);
  }, [dirty, loaded, saving, nodes, edges, caps, reqs, scope, pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await updateEntity('pipeline', scope, pipelineId, { graph: toGraph(nodes, edges), capabilities: caps, requirements: reqs, timers: timersOf(nodes, auth.currentUser?.uid || null), updatedBy: auth.currentUser?.uid || null });
      setDirty(false);
      setRunError(graphError());
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    setRunError('');
    try {
      const err = graphError();
      if (err) throw new Error(err);
      if (dirty) await save();
      const jobId = await queueFanoutJob(
        'pipeline',
        { orgId, workspaceId, pipelineId },
        { title: `pipeline: ${pipeline?.name || pipelineId}` }
      );
      await updateEntity('pipeline', scope, pipelineId, { lastRunJobId: jobId });
    } catch (e) {
      setRunError(e.message);
    }
  };

  // ---- streaming controls ----
  const toggleMode = async () => {
    setRunError('');
    try {
      await updateEntity('pipeline', scope, pipelineId, {
        mode: pipeline.mode === 'streaming' ? 'batch' : 'streaming',
        desiredState: 'stopped',
      });
    } catch (e) { setRunError(e.message); }
  };
  const setProfile = async (profile) => {
    setRunError('');
    try {
      await updateEntity('pipeline', scope, pipelineId, {
        execution: { ...(pipeline.execution || {}), profile },
      });
    } catch (e) { setRunError(e.message); }
  };
  const toggleStream = async () => {
    setRunError('');
    try {
      const starting = pipeline.desiredState !== 'running';
      if (starting) {
        const err = graphError();
        if (err) throw new Error(err);
      }
      if (dirty) await save();
      await updateEntity('pipeline', scope, pipelineId, {
        desiredState: pipeline.desiredState === 'running' ? 'stopped' : 'running',
      });
    } catch (e) { setRunError(e.message); }
  };
  const inject = async () => {
    const sources = nodes.filter((n) => !edges.some((e) => e.target === n.id)).map((n) => n.id);
    const blockId = (selected && nodes.some((n) => n.id === selected) && selected) || sources[0];
    if (!blockId) return;
    // eslint-disable-next-line no-alert
    const payload = window.prompt(`Send a document into "${nodes.find((n) => n.id === blockId)?.data.label}":`, '');
    if (payload == null || !payload.trim()) return;
    try {
      await updateEntity('pipeline', scope, pipelineId, {
        injectRequest: { blockId, payload: payload.trim(), nonce: Date.now() },
      });
    } catch (e) { setRunError(e.message); }
  };

  const jobActive = !!job && ['queued', 'planning', 'running', 'stitching'].includes(job.status);
  const liveBlocks = (jobActive ? job?.liveBlocks : null) || dryBlocks || job?.liveBlocks || null;
  const scheduled = !!(pipeline?.schedule?.enabled && pipeline?.schedule?.cron);
  const timedBlocks = nodes.filter((n) => n.data?.timer?.everyMinutes > 0);
  const nextTimerAt = Object.values(pipeline?.timerState || {}).map((s) => s?.nextAt).filter(Boolean).sort((a, b) => a - b)[0] || null;

  const streaming = pipeline?.mode === 'streaming';
  const streamOn = streaming && pipeline?.desiredState === 'running';
  const streamBlocks = (streaming && pipeline?.runtime?.blocks) || null;
  const liveWires = (streaming && pipeline?.liveWires) || null;

  // Overlay live telemetry onto the editable graph. Base nodes/edges stay the
  // editing source of truth; these derived copies just add run state for the
  // custom renderers. Batch runs use job.liveBlocks; streaming pipelines use
  // the pipeline doc's runtime/liveWires (real tasks, real queue depths).
  const rfNodes = useMemo(
    () =>
      nodes.map((n) => {
        const highlight = flagged.includes(n.id);
        if (streamBlocks) return { ...n, data: { ...n.data, highlight, stream: streamBlocks[n.id] || null } };
        if (liveBlocks?.[n.id]) return { ...n, data: { ...n.data, highlight, run: liveBlocks[n.id] } };
        return highlight ? { ...n, data: { ...n.data, highlight } } : n;
      }),
    [nodes, liveBlocks, streamBlocks, flagged]
  );
  const rfEdges = useMemo(
    () =>
      edges.map((e) => {
        if (streaming) {
          const depth = liveWires ? (liveWires[e.id] ?? 0) : null;
          return {
            ...e,
            type: 'packet',
            data: { ...(e.data || {}), onWaypoints: setWaypoints, state: streamOn && depth > 0 ? 'flowing' : 'idle', depth: streamOn ? depth : null },
          };
        }
        return {
          ...e,
          type: 'packet',
          data: { ...(e.data || {}), onWaypoints: setWaypoints, state: liveBlocks ? edgeState(liveBlocks[e.source], liveBlocks[e.target], jobActive, e.sourceHandle) : 'idle' },
        };
      }),
    [edges, liveBlocks, jobActive, streaming, streamOn, liveWires, setWaypoints]
  );

  if (pipeline === undefined) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;
  if (pipeline === null) {
    return (
      <div className="cs-page">
        <p className="cs-help">Pipeline not found. <Link to={`/orgs/${orgId}/ws/${workspaceId}`}>Back</Link></p>
      </div>
    );
  }

  const selectedNode = nodes.find((n) => n.id === selected);

  const env = pipeline.env || 'sandbox';
  return (
    <div
      className="cs-page"
      style={{ maxWidth: 'none' }}
      data-nova-describe={novaDescribe}
      data-nova-note={`nQube canvas for workflow "${pipeline.name}" (id ${pipelineId}, workspace ${workspaceId}), ${pipeline.mode === 'streaming' ? 'streaming' : 'batch'} mode, env ${env}${dirty ? ', unsaved edits (autosave pending)' : ''}${jobActive ? (testActive ? ', a TEST run (dry) is in progress' : ', a run is in progress') : ''}${pipeline.lastTest ? `, latest test ${pipeline.lastTest.status}` : ''}. NoVA edits it with edit_pipeline, explains it with describe_pipeline, declares requirements with declare_requirement, tests it with add_pipeline_test / test_pipeline (Tests card), runs it with run_pipeline, and points at blocks with focus_canvas.`}
    >
      <p style={{ margin: 0, display: 'flex', gap: 14, alignItems: 'center' }}>
        <Link to={`/orgs/${orgId}/ws/${workspaceId}`} className="cs-help" style={{ textDecoration: 'none' }}>
          ← Workspace
        </Link>
        <button
          type="button"
          className="cs-btn cs-btn-outline cs-btn-sm"
          onClick={() => setShowNew((v) => !v)}
          title="Start another workflow in this workspace"
        >
          + New workflow
        </button>
      </p>
      {showNew && (
        <NewWorkflowForm orgId={orgId} workspaceId={workspaceId} small style={{ marginTop: 8 }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        <h1 style={{ margin: 0 }}>{pipeline.name}</h1>
        {jobActive && <span className="nq-run-badge"><span className="nq-light nq-light-running" /> {job?.spec?.trigger === 'schedule' ? 'running (scheduled)' : job?.spec?.trigger === 'timer' ? 'running (block timer)' : job?.spec?.testId ? 'test running' : 'running'}</span>}
        {streamOn && <span className="nq-run-badge"><span className="nq-light nq-light-running" /> streaming live</span>}
        {timedBlocks.length > 0 && !streaming && (
          <button type="button" className="nq-run-badge" style={{ cursor: 'pointer', border: 0, font: 'inherit' }} onClick={() => setSelected(timedBlocks[0].id)} title={timedBlocks.map((n) => `${n.data.label}: every ${timerLabel(n.data.timer.everyMinutes)}`).join(' · ')}>
            ⏱ {timedBlocks.length} block timer{timedBlocks.length === 1 ? '' : 's'}{nextTimerAt ? ` · next ${Math.max(0, Math.round((nextTimerAt - Date.now()) / 60000))} min` : ''}
          </button>
        )}
        {scheduled && !streaming && (
          <button type="button" className="nq-run-badge" style={{ cursor: 'pointer', border: 0, font: 'inherit' }} onClick={() => { setSelected(null); setSideTab('schedule'); }} title={`cron ${pipeline.schedule.cron} (${pipeline.schedule.tz || 'UTC'})`}>
            ⏰ auto-run{pipeline.scheduleState?.nextAt ? ` · next ${Math.max(0, Math.round((pipeline.scheduleState.nextAt - Date.now()) / 60000))} min` : ''}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button
          className="cs-btn cs-btn-outline cs-btn-sm"
          title="Batch: run-to-completion jobs. Streaming: always-on blocks with real Redis wire queues."
          disabled={streamOn}
          onClick={toggleMode}
        >
          {streaming ? '⚡ streaming' : '▶ batch'}
        </button>
        <select
          className="cs-input"
          style={{ width: 150, padding: '4px 8px', fontSize: 13 }}
          title={EXECUTION_PROFILES[pipeline?.execution?.profile || 'realtime']?.help || 'Execution profile — how this pipeline runs (fabric, wires, storage)'}
          disabled={streamOn}
          value={pipeline?.execution?.profile || 'realtime'}
          onChange={(e) => setProfile(e.target.value)}
        >
          {Object.entries(EXECUTION_PROFILES).map(([k, p]) => (
            <option key={k} value={k}>{p.label || k}</option>
          ))}
        </select>
        <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!dirty || saving} onClick={save} title="Changes save automatically a moment after you stop editing">
          {saving ? 'Saving…' : dirty ? 'Save now' : 'Saved ✓'}
        </button>
        <span className="cs-help" style={{ margin: 0, fontSize: 12 }}>{nodes.length} block{nodes.length === 1 ? '' : 's'}</span>
        {streaming ? (
          <>
            <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!streamOn} onClick={inject}>
              ⇥ Inject document
            </button>
            <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!nodes.length} onClick={toggleStream}>
              {streamOn ? 'Stop' : 'Start'}
            </button>
          </>
        ) : (
          <button
            className="cs-btn cs-btn-primary cs-btn-sm"
            disabled={!nodes.length || jobActive}
            onClick={run}
            {...(env !== 'sandbox' ? { 'data-agent-guard': 'true' } : {})}
            title={env !== 'sandbox' ? `Runs in ${env} touch real systems — a person presses this` : 'Run the workflow once in sandbox'}
          >
            Run
          </button>
        )}
      </div>
      {runError && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{runError}</p>}
      {pipeline.learn && (
        <div className="cs-card" style={{ marginTop: 10, padding: '10px 12px', borderLeft: '3px solid #22d3ee' }}>
          <span className="mf-chip is-info" style={{ marginRight: 8 }}>{pipeline.example ? 'example' : 'notes'}</span>
          <span className="cs-help" style={{ fontSize: 13 }}>{pipeline.learn}</span>
          {pipeline.example && (
            <span className="cs-help" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Examples are shared with the org — Duplicate from the Workflows list to make an editable copy.
            </span>
          )}
        </div>
      )}

      {/* palette | canvas | config — restacks in a narrow column (.nq-editor rules). */}
      <div className="nq-editor">
        {/* Palette — grouped by what a block does, in pipeline order */}
        <div className="nq-editor-palette">
          <span className="cs-help" style={{ fontSize: 12 }}>Add block</span>
          <select
            className="cs-input" style={{ width: '100%', padding: '2px 6px', fontSize: 11.5, marginTop: 4 }} aria-label="wire new blocks from"
            value={wireFrom} onChange={(e) => setWireFrom(e.target.value)}
            title="Where a new block is wired from"
          >
            <option value="last">wire from: last block</option>
            <option value="selected">wire from: selected block</option>
            <option value="none">wire from: nothing (island)</option>
          </select>
          {CATEGORY_ORDER.map(([cat, heading]) => {
            const items = Object.entries(BLOCK_TYPES).filter(([, def]) => (def.category || 'transform') === cat);
            if (!items.length) return null;
            return (
              <div key={cat} style={{ marginTop: 10 }}>
                <span className="nq-palette-head" style={{ color: CATEGORY_COLOR[cat] }}>{heading}</span>
                <div className="nq-palette-items">
                  {items.map(([type, def]) => (
                    <button
                      key={type}
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      style={{ borderColor: CATEGORY_COLOR[def.category], textAlign: 'left' }}
                      title={def.description}
                      onClick={() => addBlock(type)}
                    >
                      {def.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Canvas */}
        <div className="nq-editor-canvas">
          <div ref={canvasRef} className="cs-card" style={{ height: 520, padding: 0, overflow: 'hidden', position: 'relative' }} data-nova-ignore="true" aria-label="Workflow canvas">
            {loaded && !nodes.length && (
              <div className="nq-empty">
                <strong>Empty canvas</strong>
                <p className="cs-help" style={{ margin: '6px 0 10px' }}>
                  A pipeline reads something in, transforms it, passes a gate, then writes somewhere.
                  Add blocks from the left, or start from the template.
                </p>
                <button className="cs-btn cs-btn-primary cs-btn-sm" type="button" onClick={addStarter}>
                  Start from template: Text → LLM → Human review → Webhook
                </button>
              </div>
            )}
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={(_e, n) => { setSelected(n.id); setFlagged([]); }}
              onPaneClick={() => { setSelected(null); setFlagged([]); }}
              onEdgeDoubleClick={onEdgeDoubleClick}
              onInit={onFlowInit}
              minZoom={minZoom}
              maxZoom={maxZoom}
              colorMode="dark"
              proOptions={{ hideAttribution: true }}
            >
              <FlowZoomBridge useStoreApi={useStoreApi} placeRef={placeRef} />
              <Background />
              <Controls />
              <MiniMap
                pannable
                zoomable
                nodeColor={(n) => CATEGORY_COLOR[BLOCK_TYPES[n.data?.blockType]?.category] || '#475569'}
                maskColor="rgba(2,6,12,0.55)"
                style={{ background: 'rgba(10,14,20,0.9)' }}
              />
            </ReactFlow>
          </div>
          {(job || streamOn) && (
          <div className="nq-legend">
            <span><i className="nq-light nq-light-queued" /> queued</span>
            <span><i className="nq-light nq-light-running" /> processing</span>
            <span><i className="nq-light nq-light-done" /> done</span>
            <span><i className="nq-light nq-light-skipped" /> skipped branch</span>
            <span><svg width="26" height="8"><line x1="0" y1="4" x2="26" y2="4" stroke="#22d3ee" strokeWidth="2" /><circle cx="13" cy="4" r="3" fill="#22d3ee" /></svg> data in flight</span>
          </div>
          )}
          {!job && !streamOn && nodes.length > 0 && (
            <p className="cs-help" style={{ marginTop: 8, fontSize: 12 }}>
              Drag from a block&apos;s right edge to the next block&apos;s left edge to connect them. Click a block to configure it. Double-click a wire to add a bend point.
              {dirty ? ' Changes save automatically.' : ''}
            </p>
          )}
        </div>

        {/* Config panel */}
        <div className="nq-editor-config">
          <div className="cs-card" style={{ padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ color: 'var(--text)', fontSize: 13 }}>Kind</strong>
              <select
                className="cs-input" style={{ padding: '2px 6px', fontSize: 12 }} aria-label="Pipeline kind"
                value={pipeline.kind || 'pipeline'}
                onChange={(e) => updateEntity('pipeline', scope, pipelineId, { kind: e.target.value }).catch((err) => setRunError(err.message))}
              >
                <option value="pipeline">pipeline</option>
                <option value="router">router (Dev Console routing policy)</option>
              </select>
            </div>
            <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>
              {(pipeline.kind || 'pipeline') === 'router'
                ? 'Decides which model tier a Dev Console request gets on the hosted instance: Call input → Switch / Decision → Return {"tier","mode","reason"}. No side effects. Ready routers in this workspace ship with the app bundle; the instance routes on one when its Dev Console engine is set to pipeline.'
                : 'A workflow: runs on a schedule, a trigger, or by hand; may write to systems through capabilities.'}
            </p>
          </div>
          <CapabilitiesPanel
            caps={caps}
            onChange={(next) => { setCaps(next); setDirty(true); }}
            env={pipeline.env || 'sandbox'}
            onEnv={(env) => updateEntity('pipeline', scope, pipelineId, { env }).then(() => setRunError(graphError())).catch((e) => setRunError(e.message))}
            integrations={integrations || []}
            orgId={orgId}
          />
          <div
            className="cs-card"
            style={{ padding: 12, marginBottom: 10 }}
            data-nova-note={reqResults.length
              ? `Requirements card: ${reqSummary.met} met, ${reqSummary.unmet} not met, ${reqSummary['needs-run']} need a run. ${reqResults.filter((r) => r.status !== 'met').map((r) => `"${r.text}" is ${r.status === 'unmet' ? 'NOT met' : 'waiting for a run'}: ${r.detail}`).join(' ').slice(0, 140)}`
              : 'Requirements card: none declared yet — "Add requirement" or ask NoVA (declare_requirement) to say what this design must guarantee.'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ color: 'var(--text)', fontSize: 13 }}>Requirements</strong>
              <span style={{ flex: 1 }} />
              {reqSummary.total > 0 && (
                <span className="cs-help" style={{ fontSize: 11.5, margin: 0 }}>
                  {reqSummary.met} met{reqSummary.unmet ? ` · ${reqSummary.unmet} not met` : ''}{reqSummary['needs-run'] ? ` · ${reqSummary['needs-run']} need a run` : ''}
                </span>
              )}
              {reqSummary.total > 0 && <StatusChip status={reqSummary.unmet ? 'unmet' : reqSummary['needs-run'] ? 'needs-run' : 'met'} />}
            </div>
            <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>
              What this service must guarantee — approvals, data integrity, performance, completeness. Checked against the drawing as you edit; a miss blocks runs in UAT and prod and is only shown in sandbox.
            </p>
            <RequirementsEditor
              rows={reqs}
              onChange={(next) => { setReqs(next); setDirty(true); }}
              results={reqResults}
              fixed={reqResults.filter((r) => r.source !== 'pipeline')}
              onFocus={(ids) => { setFlagged(ids); setSelected(null); }}
            />
          </div>
          {!streaming && (
            <DryRunPanel
              dry={dry}
              onRun={dryRun}
              hasCallInput={nodes.some((n) => n.data.blockType === 'input.call')}
              hasBatchInput={nodes.some((n) => ['input.dataset', 'input.upload', 'input.s3list'].includes(n.data.blockType))}
            />
          )}
          {!streaming && (
            <PipelineTestsCard
              pipeline={pipeline}
              graph={toGraph(nodes, edges)}
              disabled={jobActive}
              onChangeTests={(tests) => updateEntity('pipeline', scope, pipelineId, { tests }).catch((e) => setRunError(e.message))}
              onFocus={(ids) => { setFlagged(ids); setSelected(null); }}
              onRunTest={async (t) => {
                const err = graphError();
                if (err) throw new Error(err);
                if (dirty) await save();
                await queueFanoutJob(
                  'pipeline',
                  { orgId, workspaceId, pipelineId, testId: t.id, dryRun: true },
                  { title: `test: ${pipeline?.name || pipelineId} · ${t.name}` }
                );
              }}
            />
          )}
          {selectedNode ? (
            <BlockConfig
              node={selectedNode}
              caps={caps}
              integrations={integrations || []}
              timerState={pipeline?.timerState?.[selectedNode.id] || null}
              orgId={orgId}
              workspaceId={workspaceId}
              pipelineId={pipelineId}
              nodes={nodes}
              edges={edges}
              onWire={(fromId, on) => {
                const id = `${fromId}-${selectedNode.id}`;
                setEdges((es) => (on ? (es.some((e) => e.source === fromId && e.target === selectedNode.id) ? es : [...es, ...toEdges({ connections: [{ id, from: fromId, to: selectedNode.id }] })]) : es.filter((e) => !(e.source === fromId && e.target === selectedNode.id))));
                setDirty(true);
              }}
              onDuplicate={() => duplicateBlock(selectedNode)}
              onShowLogs={() => { setLogBlock(selectedNode.id); setSelected(null); setSideTab('logs'); }}
              onChange={(patch) => {
                setNodes((ns) => ns.map((n) => (n.id === selectedNode.id ? { ...n, data: { ...n.data, ...patch } } : n)));
                setDirty(true);
              }}
              onDelete={() => {
                setNodes((ns) => ns.filter((n) => n.id !== selectedNode.id));
                setEdges((es) => es.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
                setSelected(null);
                setDirty(true);
              }}
            />
          ) : (
            <div className="cs-card" style={{ padding: 12 }}>
              <div className="nq-tabs" role="tablist">
                {[['run', 'Run'], ['logs', 'Logs'], ['history', 'History'], ...(streaming ? [] : [['schedule', 'Schedule']])].map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={sideTab === k} className={`nq-tab${sideTab === k ? ' is-active' : ''}`} onClick={() => setSideTab(k)}>{l}</button>
                ))}
              </div>
              {sideTab === 'run' && <RunStatus job={job} tasks={tasks} pipeline={pipeline} bare />}
              {sideTab === 'logs' && (
                <RunLogsPanel
                  jobId={job?.id || pipeline.lastRunJobId || null}
                  orgId={orgId} workspaceId={workspaceId} pipelineId={pipelineId}
                  blocks={toGraph(nodes, edges).blocks}
                  selectedBlock={logBlock} onSelectBlock={setLogBlock}
                  active={jobActive}
                />
              )}
              {sideTab === 'history' && (
                <HistoryPanel
                  orgId={orgId} workspaceId={workspaceId} pipelineId={pipelineId} pipeline={pipeline}
                  onRestored={() => { setLoaded(false); setDirty(false); }}
                  onError={(m) => setRunError(m)}
                />
              )}
              {sideTab === 'schedule' && (
                <SchedulePanel
                  pipeline={pipeline} orgId={orgId} workspaceId={workspaceId}
                  canEdit={canEditSchedule}
                  uid={auth.currentUser?.uid || null}
                  hasCallInput={nodes.some((n) => n.data.blockType === 'input.call')}
                  onChange={(patch) => updateEntity('pipeline', scope, pipelineId, patch).catch((e) => setRunError(e.message))}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CAP_KINDS = [
  ['s3', 'S3 prefix'],
  ['dataset', 'Dataset'],
  ['knowledge', 'Knowledge audience'],
  ['webhook', 'Webhook URL'],
  ['container', 'Container image prefix'],
  ['code', 'Code preset (python-ds | torch-gpu | detectron2 | *)'],
];

const ENV_HELP = {
  sandbox: 'Sandbox: any member may run; keyword filters count as gates.',
  uat: 'UAT: run access per Administration → environment controls.',
  prod: 'Prod: only a Human review gates a model → side effect; running needs the prod role.',
};

function CapabilitiesPanel({ caps, onChange, env, onEnv, integrations, orgId }) {
  const patch = (i, fields) => {
    onChange(caps.map((c, idx) => (idx === i ? { ...c, ...fields } : c)));
  };
  // Webhook capabilities bind to an admin-owned Integration: picking one
  // fills the target base + secret name from it; the target may only extend
  // the Integration's base URL.
  const hooks = (integrations || []).filter((it) => (it.kind === 'webhook' || it.kind === 'http') && it.enabled !== false);
  const bind = (i, integrationId) => {
    const it = hooks.find((h) => h.id === integrationId);
    if (!it) return patch(i, { integrationId: '' });
    const base = String(it.baseUrl || '').replace(/\/$/, '');
    const action = (it.actions || []).find((a) => a.path) || null;
    return patch(i, {
      integrationId: it.id,
      target: base + (action ? action.path : ''),
      secretKey: it.secretKey || '',
    });
  };
  return (
    <div className="cs-card" style={{ padding: 12, marginBottom: 10 }} data-nova-note={`Environment ${env}; ${caps.length} capabilit${caps.length === 1 ? 'y' : 'ies'} declared${caps.length ? `: ${caps.map((c) => `${c.id} (${c.kind})`).join(', ')}` : ' — side-effect blocks need one (declare_capability)'}.`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>Environment</strong>
        <select className="cs-input" style={{ padding: '2px 6px', fontSize: 12 }} value={env} onChange={(e) => onEnv(e.target.value)} aria-label="Pipeline environment" title={ENV_HELP[env]}>
          {['sandbox', 'uat', 'prod'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>{ENV_HELP[env] || ENV_HELP.sandbox}</p>
      <strong style={{ color: 'var(--text)', fontSize: 13, display: 'block', marginTop: 10 }}>Capabilities</strong>
      <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
        Systems this pipeline may write to. A side effect has to name one. Webhooks bind to an Integration an admin set up for this environment; secrets stay in SSM.
      </p>
      {caps.map((c, i) => (
        <div key={i} style={{ display: 'grid', gap: 4, marginTop: 8 }}>
          <input className="cs-input" placeholder="id" value={c.id || ''} onChange={(e) => patch(i, { id: e.target.value })} />
          <select className="cs-input" value={c.kind || 'webhook'} onChange={(e) => patch(i, { kind: e.target.value })}>
            {CAP_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          {c.kind === 'webhook' && (
            <select className="cs-input" value={c.integrationId || ''} onChange={(e) => bind(i, e.target.value)} aria-label="Integration">
              <option value="">— pick an Integration —</option>
              {hooks.map((h) => (
                <option key={h.id} value={h.id} disabled={(h.scope || 'sandbox') !== env}>
                  {h.name} · {h.scope || 'sandbox'}{(h.scope || 'sandbox') !== env ? ' (other env)' : ''}
                </option>
              ))}
            </select>
          )}
          {c.kind === 'webhook' && !hooks.some((h) => (h.scope || 'sandbox') === env) && (
            <span className="cs-help" style={{ fontSize: 11.5 }}>
              No {env} Integration yet — an admin adds one in <Link to={`/orgs/${orgId}/integrations`}>Integrations</Link>.
            </span>
          )}
          <input
            className="cs-input"
            placeholder={c.kind === 'webhook' ? 'https://…' : 'target'}
            value={c.target || ''}
            onChange={(e) => patch(i, { target: e.target.value })}
          />
          {c.kind === 'webhook' && (
            <input
              className="cs-input"
              placeholder="secret name"
              value={c.secretKey || ''}
              onChange={(e) => patch(i, { secretKey: e.target.value })}
            />
          )}
          <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={() => onChange(caps.filter((_, idx) => idx !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button
        className="cs-btn cs-btn-outline cs-btn-sm"
        type="button"
        style={{ marginTop: 8 }}
        onClick={() => onChange([...caps, { id: `cap${caps.length + 1}`, kind: 'webhook', target: '', secretKey: '' }])}
      >
        Add capability
      </button>
    </div>
  );
}

const TIMER_EVERY = [[0, 'off — runs with the pipeline'], [1, 'every minute'], [5, 'every 5 min'], [15, 'every 15 min'], [30, 'every 30 min'], [60, 'hourly'], [360, 'every 6 h'], [720, 'every 12 h'], [1440, 'daily']];
const TIMER_TIMEOUT = [[0, 'no timeout'], [1, '1 min max'], [5, '5 min max'], [15, '15 min max'], [60, '1 h max'], [180, '3 h max']];
const timerLabel = (m) => (m >= 1440 ? `${Math.round(m / 1440)} d` : m >= 60 ? `${Math.round(m / 60)} h` : `${m} min`);

function BlockConfig({ node, caps, integrations = [], timerState = null, onChange, onDelete, orgId, workspaceId, pipelineId, nodes = [], edges = [], onWire, onDuplicate, onShowLogs }) {
  const def = BLOCK_TYPES[node.data.blockType] || { config: [] };
  const config = node.data.config || {};
  const compute = node.data.compute || { class: 'shared' };
  const sizedClass = compute.class === 'standard' || compute.class === 'heavy';
  const isCode = node.data.blockType === 'code.python';
  const isUpload = node.data.blockType === 'input.upload';
  const retry = node.data.retry || { maxAttempts: 1 };
  const timer = node.data.timer || {};
  const ownClock = ['container.run', 'control.preflight', 'code.python'].includes(node.data.blockType); // config.timeoutMinutes stops the task itself
  const isOutput = /^output\./.test(node.data.blockType);
  // S3 connections (Integrations → S3 bucket): the keys for a bucket in
  // another account live there; the block just names the connection.
  const s3Connections = (integrations || []).filter((i) => i.kind === 's3' && i.enabled !== false);
  // Wire-from picker: every other non-output block, checked when it feeds this one.
  const upstreamChoices = nodes.filter((n) => n.id !== node.id && !/^output\./.test(n.data?.blockType || ''));
  const feeds = new Set(edges.filter((e) => e.target === node.id).map((e) => e.source));
  return (
    <div className="cs-card" style={{ padding: 12 }} data-nova-note={`Block config open for ${node.id} (${node.data.blockType} "${node.data.label}")${node.data.classification === 'sensitive' ? ', classified sensitive' : ''}. Fields below are this block's settings; edit_pipeline configure_block sets them, remove_block deletes it.`}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>{def.label || node.data.blockType}</strong>
        <span style={{ flex: 1 }} />
        {onShowLogs && <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={onShowLogs} title="This block's log from the last run">Logs</button>}
        {onDuplicate && <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={onDuplicate} title="Duplicate this block (same settings, new id, unwired)">⧉</button>}
        <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={onDelete} data-agent-guard="true" title="Remove this block (edges go with it)">✕</button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
        <span className="cs-help" style={{ fontSize: 11, margin: 0, fontFamily: 'ui-monospace, monospace' }} title="block id">{node.id}</span>
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" style={{ padding: '0 6px', fontSize: 11 }} onClick={() => copyText(node.id)} title="Copy the block id">copy</button>
      </div>
      {def.description && <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>{def.description}</p>}
      {node.data.blockType === 'control.review' && (
        <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
          A person approves (or edits) each document here before it continues. Items wait in the{' '}
          <Link to={`/orgs/${orgId}/review`}>Review</Link> queue.
        </p>
      )}

      <label className="cs-help" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>Label</label>
      <input
        className="cs-input"
        style={{ width: '100%', marginTop: 4 }}
        value={node.data.label}
        onChange={(e) => onChange({ label: e.target.value })}
      />

      {isCode && (
        <CodeBlockFields node={node} config={config} onConfig={(c) => onChange({ config: c })} orgId={orgId} workspaceId={workspaceId} pipelineId={pipelineId} />
      )}
      {(isCode ? (def.config || []).filter((f) => f.key === 'capabilityId') : (def.config || [])).map((f) => (
        <div key={f.key}>
          <label className="cs-help" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
            {f.label}
            {f.help ? ` — ${f.help}` : ''}
          </label>
          {f.key === 'capabilityId' ? (
            <>
              <select
                className="cs-input"
                style={{ width: '100%', marginTop: 4 }}
                value={config.capabilityId || ''}
                onChange={(e) => onChange({ config: { ...config, capabilityId: e.target.value } })}
              >
                <option value="">Select a capability</option>
                {(caps || []).filter((c) => c.kind === sideEffectKind(node.data.blockType)).map((c) => (
                  <option key={c.id} value={c.id}>{c.id} · {c.target || c.kind}</option>
                ))}
              </select>
              {!(caps || []).some((c) => c.kind === sideEffectKind(node.data.blockType)) && (
                <p className="cs-help nq-soft-warn" style={{ fontSize: 12, marginTop: 4 }}>
                  This block writes outside the pipeline, so it needs a <strong>{sideEffectKind(node.data.blockType)}</strong> capability —
                  add one in the Capabilities panel above (the URL or target, and for webhooks the name of the secret). The run refuses to start without it.
                </p>
              )}
            </>
          ) : f.key === 'integrationId' ? (
            <>
              <select
                className="cs-input"
                style={{ width: '100%', marginTop: 4 }}
                value={config.integrationId || ''}
                onChange={(e) => onChange({ config: { ...config, integrationId: e.target.value } })}
                aria-label="S3 connection"
              >
                <option value="">This project's buckets</option>
                {s3Connections.map((c) => <option key={c.id} value={c.id}>{c.name} · s3://{c.config?.bucket || '?'}{c.config?.prefix ? `/${c.config.prefix}` : ''} ({c.config?.region || 'region?'})</option>)}
                {config.integrationId && !s3Connections.some((c) => c.id === config.integrationId) && <option value={config.integrationId}>{config.integrationId} (missing or disabled)</option>}
              </select>
              <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>
                Another account's bucket: an admin adds its access key under <Link to={`/orgs/${orgId}/integrations`}>Integrations → S3 bucket</Link>; the secret stays in the secret store and this block only names the connection.
              </p>
            </>
          ) : f.type === 'textarea' ? (
            <>
              <textarea
                className="cs-input"
                style={{ width: '100%', minHeight: 80, marginTop: 4 }}
                value={config[f.key] || ''}
                onChange={(e) => onChange({ config: { ...config, [f.key]: e.target.value } })}
              />
              {!(config[f.key] || '').trim() && (f.key === 'prompt' || f.key === 'template') && (
                <p className="cs-help nq-soft-warn" style={{ fontSize: 12, marginTop: 4 }}>
                  Empty — this block will pass its input through unchanged. Say what it should do.
                </p>
              )}
            </>
          ) : f.type === 'select' ? (
            <select
              className="cs-input"
              style={{ width: '100%', marginTop: 4 }}
              value={config[f.key] || f.options?.[0] || ''}
              onChange={(e) => onChange({ config: { ...config, [f.key]: e.target.value } })}
            >
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              className="cs-input"
              style={{ width: '100%', marginTop: 4 }}
              value={config[f.key] || ''}
              onChange={(e) => onChange({ config: { ...config, [f.key]: e.target.value } })}
            />
          )}
        </div>
      ))}

      {isUpload && (
        <UploadBlockFields node={node} config={config} onConfig={(c) => onChange({ config: c })} orgId={orgId} pipelineId={pipelineId} />
      )}

      {onWire && upstreamChoices.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary className="cs-help" style={{ fontSize: 12, cursor: 'pointer' }}>Inputs — fed by {feeds.size ? [...feeds].map((id) => nodes.find((n) => n.id === id)?.data.label || id).join(', ') : 'nothing yet'}</summary>
          <div style={{ display: 'grid', gap: 4, marginTop: 6 }}>
            {upstreamChoices.map((n) => (
              <label key={n.id} className="cs-help" style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
                <input type="checkbox" checked={feeds.has(n.id)} onChange={(e) => onWire(n.id, e.target.checked)} /> {n.data.label}
              </label>
            ))}
          </div>
        </details>
      )}

      <label className="cs-help" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Retry on failure
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select className="cs-input" style={{ flex: 1, marginTop: 4 }} value={retry.maxAttempts || 1} onChange={(e) => onChange({ retry: { ...retry, maxAttempts: Number(e.target.value) } })} aria-label="attempts">
          {[1, 2, 3, 5].map((n) => <option key={n} value={n}>{n === 1 ? 'no retry' : `up to ${n} attempts`}</option>)}
        </select>
        {retry.maxAttempts > 1 && (
          <select className="cs-input" style={{ width: 110, marginTop: 4 }} value={retry.backoffMs || 5000} onChange={(e) => onChange({ retry: { ...retry, backoffMs: Number(e.target.value) } })} aria-label="backoff">
            {[[1000, '1 s'], [5000, '5 s'], [30000, '30 s'], [120000, '2 min']].map(([v, l]) => <option key={v} value={v}>{l} wait</option>)}
          </select>
        )}
      </div>

      <label className="cs-help" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Timer — auto-run this block
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          className="cs-input"
          style={{ flex: 1, marginTop: 4 }}
          value={timer.everyMinutes || 0}
          onChange={(e) => onChange({ timer: { ...timer, everyMinutes: Number(e.target.value) } })}
          aria-label="auto-run interval"
          disabled={isOutput}
        >
          {TIMER_EVERY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {!ownClock && (
          <select
            className="cs-input"
            style={{ width: 130, marginTop: 4 }}
            value={timer.timeoutMinutes || 0}
            onChange={(e) => onChange({ timer: { ...timer, timeoutMinutes: Number(e.target.value) } })}
            aria-label="block timeout"
            title="Wall clock for this block — it fails (and retries per the policy above) when it runs longer"
          >
            {TIMER_TIMEOUT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
      </div>
      {timer.everyMinutes > 0 && (
        <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>
          Every {timerLabel(timer.everyMinutes)} this block and everything downstream of it run again; blocks upstream reuse their output from the last run (one that never ran, or whose output is too big to keep, runs again too).
          {timerState?.nextAt ? ` Next ${new Date(timerState.nextAt).toLocaleString()}.` : ' Arming after save.'}
          {timerState?.firedCount ? ` ${timerState.firedCount} timer run${timerState.firedCount === 1 ? '' : 's'}${timerState.skippedCount ? `, ${timerState.skippedCount} skipped (previous still running)` : ''}.` : ''}
          {timerState?.lastError ? ` Last error: ${timerState.lastError}` : ''}
        </p>
      )}
      {isOutput && <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>Put the timer on the block that produces the data; a writer re-runs with it.</p>}

      <label className="cs-help" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Classification — what this block&apos;s data is
      </label>
      <select
        className="cs-input"
        style={{ width: '100%', marginTop: 4 }}
        value={node.data.classification || ''}
        onChange={(e) => onChange({ classification: e.target.value || null })}
      >
        <option value="">ordinary</option>
        <option value="sensitive">sensitive — the &quot;sensitive stays local&quot; requirement watches where it flows</option>
      </select>

      {node.data.blockType !== 'container.run' && !isCode && (
        <>
          <label className="cs-help" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
            Compute — where this block executes
          </label>
          <select
            className="cs-input"
            style={{ width: '100%', marginTop: 4 }}
            value={compute.class || 'shared'}
            onChange={(e) => onChange({ compute: { ...compute, class: e.target.value } })}
          >
            {COMPUTE_CLASSES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          {sizedClass && (
            <select
              className="cs-input"
              style={{ width: '100%', marginTop: 6 }}
              value={compute.size || 'M'}
              onChange={(e) => onChange({ compute: { ...compute, size: e.target.value } })}
            >
              {['S', 'M', 'L', 'XL'].map((s) => <option key={s} value={s}>Size {s}</option>)}
            </select>
          )}
        </>
      )}
    </div>
  );
}

function RunStatus({ job, tasks, pipeline, bare = false }) {
  const Card = ({ children }) => (bare ? <div>{children}</div> : <div className="cs-card" style={{ padding: 12 }}>{children}</div>);
  if (pipeline?.mode === 'streaming') {
    const rt = pipeline.runtime?.blocks || {};
    const blocks = (pipeline.graph?.blocks || []).map((b) => ({ ...b, s: rt[b.id] || {} }));
    const totalQueued = Object.values(pipeline.liveWires || {}).reduce((a, n) => a + n, 0);
    const prof = EXECUTION_PROFILES[pipeline.execution?.profile || 'realtime'] || {};
    const fabric = pipeline.execution?.fabric || prof.fabric || 'dedicated';
    const transport = fabric === 'host' ? 'memory' : (pipeline.execution?.transport || prof.transport || 'redis');
    return (
      <Card>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>
          Streaming — {pipeline.desiredState === 'running' ? 'live' : 'stopped'}
        </strong>
        <p className="cs-help" style={{ fontSize: 12, margin: '4px 0 0' }}>
          {fabric === 'host' ? 'host fabric (one task)' : 'dedicated fabric (task per block)'} · {transport} wires
        </p>
        {pipeline.runtime?.host?.lastError && (
          <p className="cs-help" style={{ fontSize: 12, margin: '6px 0 0', color: 'var(--danger, #e5484d)' }}>
            {pipeline.runtime.host.lastError}
          </p>
        )}
        {pipeline.desiredState === 'running' && (
          <p className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
            {totalQueued} document{totalQueued === 1 ? '' : 's'} in flight on the wires
          </p>
        )}
        {blocks.map((b) => (
          <p key={b.id} className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
            <span className={`nq-light nq-light-${STREAM_LIGHT[b.s.status] || 'skipped'}`} style={{ marginRight: 6 }} />
            {b.label || b.type}: {b.s.status || 'stopped'}
            {typeof b.s.processed === 'number' ? ` · ${b.s.processed} docs` : ''}
            {b.s.errors ? ` · ${b.s.errors} err` : ''}
          </p>
        ))}
        <p className="cs-help" style={{ fontSize: 11, marginTop: 10 }}>
          Every block is a live Fargate task; wire numbers are real Redis queue depths.
        </p>
      </Card>
    );
  }
  if (!job) {
    return (
      <Card>
        <p className="cs-help" style={{ margin: 0, fontSize: 12 }}>
          Select a block to configure it. Run status appears here.
        </p>
      </Card>
    );
  }
  const results = job.output?.results || [];
  const live = job.liveBlocks || {};
  const counts = Object.values(live).reduce((acc, b) => {
    const k = b.gateClosed ? 'done' : b.status;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  return (
    <Card>
      <strong style={{ color: 'var(--text)', fontSize: 13 }}>Last run — {job.status}{job.spec?.trigger === 'schedule' ? ' · scheduled' : job.spec?.trigger === 'timer' ? ` · block timer${job.info?.replayed ? ` (${job.info.replayed} replayed)` : ''}` : ''}{job.revisionId ? ` · design ${job.revisionId}` : ''}</strong>
      {Object.keys(live).length > 0 && (
        <p className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {['running', 'queued', 'done', 'skipped', 'error']
            .filter((k) => counts[k])
            .map((k) => `${counts[k]} ${k}`)
            .join(' · ')}
        </p>
      )}
      {(tasks || []).map((t) => (
        <p key={t.id} className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {t.role}: {t.stage || t.status}
        </p>
      ))}
      {Array.isArray(job.swarmIds) && job.swarmIds.length > 0 && job.spec?.orgId && (
        <p className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {job.swarmIds.length} coordinator swarm{job.swarmIds.length === 1 ? '' : 's'} in this run —{' '}
          <Link to={`/orgs/${job.spec.orgId}/workflows?tab=agents`}>watch in Agent IO</Link>
        </p>
      )}
      {results.map((r) => (
        <p key={r.blockId} className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {r.skipped ? '⤳' : r.ok ? '✓' : '✗'} {r.label || r.type}
          {r.skipped ? ' — skipped (branch not taken)' : ''}
          {r.replayed ? ' — replayed from the previous run (block timer)' : ''}
          {r.retry ? ` — ${r.retry} retr${r.retry === 1 ? 'y' : 'ies'}` : ''}
          {r.code?.sha ? ` — code ${String(r.code.sha).slice(0, 10)}` : ''}
          {r.error ? ` — ${r.error}` : ''}
        </p>
      ))}
      {job.output?.outputs && (
        <details style={{ marginTop: 8 }}>
          <summary className="cs-help" style={{ fontSize: 12, cursor: 'pointer' }}>Block outputs</summary>
          <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200 }}>
            {JSON.stringify(job.output.outputs, null, 2)}
          </pre>
        </details>
      )}
    </Card>
  );
}
